<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { ref, watch } from 'vue'
import { updateUserStatusAction } from '@/api/functions/users/update-user-status-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const emit = defineEmits(['refreshUserData'])

const props = defineProps({
  userData: {
    type: Object,
    required: true,
  },
  userStatusOptions: {
    type: Array,
    required: true,
  },
  uuid: {
    type: String,
    required: true,
  },
})

const toast = useToast()
const queryClient = useQueryClient()

const selectedStatus = ref<string>(props.userData?.status || '')

watch(
  () => props.userData,
  (newVal) => {
    selectedStatus.value = newVal?.status || ''
  },
  { deep: true, immediate: true }
)

const isUpdating = ref(false)

const updateStatusMutation = useMutationApi({
  mutationFn: (status: string) => updateUserStatusAction(props.uuid, status),

  onMutate: () => {
    isUpdating.value = true
    toast.info('در حال به‌روزرسانی وضعیت...')
  },

  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'وضعیت کاربر با موفقیت به‌روزرسانی شد')
    queryClient.invalidateQueries({ queryKey: ['user', props.uuid] })
    emit('refreshUserData')
  },

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در تغییر وضعیت')
  },

  onSettled: () => {
    isUpdating.value = false
  },
})

const handleUpdateStatus = () => {
  if (!selectedStatus.value) {
    toast.error('لطفاً وضعیت را انتخاب کنید')
    return
  }
  updateStatusMutation.mutate(selectedStatus.value)
}
</script>

<template>
    <div class="bg-white rounded-xl flex flex-col lg:grid lg:grid-cols-12 my-6 lg:h-28 p-8 gap-6">
        <div class="col-span-4 w-full flex items-center justify-between text-sm text-nowrap">
            <span class="text-[#2d5396]">وضعیت ثبت نام:</span>
            <span class="font-fa text-[#1b325a]">
                {{
                    userData?.status === 'VERIFIED'
                        ? 'تایید شده'
                        : userData?.status === 'EMAIL_VERIFICATION_PENDING'
                            ? 'ثبت نام اولیه'
                            : userData?.status === 'MOBILE_REGISTRATION_PENDING'
                                ? 'در انتظار ثبت شماره موبایل'
                                : userData?.status === 'IDENTIFICATION_REGISTRATION_PENDING'
                                    ? 'در انتظار ارسال مدارک هویتی'
                                    : userData?.status === 'IDENTIFICATION_VERIFICATION_PENDING'
                                        ? 'در انتظار تایید مدارک هویتی'
                                        : userData?.status === 'MOBILE_VERIFICATION_PENDING'
                                            ? 'در انتظار تایید شماره موبایل'
                : ''
                }}
            </span>
        </div>

        <div class="col-span-5 w-full flex items-center justify-between text-sm">
            <span class="text-[#2d5396] text-nowrap">بازگرداندن وضعیت به:</span>
            <Select variant="checkbox" v-model="selectedStatus" label="انتخاب وضعیت" class="w-[211px]! h-12!"
                :options="userStatusOptions" />
        </div>

        <div class="col-span-3 flex items-center justify-end">
            <Button :isAdmin="true" @click="handleUpdateStatus" :disabled="!selectedStatus || isUpdating"
                class="w-auto rounded-[10px] px-5 h-10! py-0 text-[13px] font-normal me-2">
                <span v-if="!isUpdating">ویرایش</span>
                <span v-else>در حال به‌روزرسانی...</span>
            </Button>
        </div>
    </div>
</template>
